import { useEffect, useRef } from "react";
import { animate, useMotionValue, useTransform } from "framer-motion";

const mix = (a, b, p) => a + (b - a) * p;
const position = (g, progress, flight, unlocked, reduced) => {
  if (!g) return { x: 0, y: 0, scale: 1, angle: 0 };
  const tracking = { x: g.start + progress * g.length, y: g.y, scale: 1, angle: 0 };
  if (!unlocked || reduced || flight <= 0.23) return tracking;
  if (flight < 0.6) {
    const t = ((flight - 0.23) / 0.37) ** 2;
    return { x: mix(g.start + g.length, g.moonX, t), y: mix(g.y, g.moonY, t), scale: mix(1, 0.82, t), angle: -6 * Math.sin(t * Math.PI) };
  }
  const t = Math.min(1, (flight - 0.6) / 0.4);
  return { x: mix(g.moonX, g.beyondX, t), y: mix(g.moonY, g.beyondY, t), scale: mix(0.82, 0.4, t), angle: -6 };
};

export const useLaunchFlight = ({ geometry, displayed, isUnlocked, reduced, running, celebrating, celebrationCount }) => {
  const flight = useMotionValue(isUnlocked ? 1 : 0);
  const played = useRef(0);
  useEffect(() => {
    if (!isUnlocked) { flight.set(0); return; }
    if (reduced || !running) { flight.set(1); return; }
    if (celebrating && celebrationCount > played.current) {
      played.current = celebrationCount;
      flight.set(0);
      const animation = animate(flight, 1, { duration: 4.8, ease: "linear" });
      return () => animation.stop();
    }
    if (!celebrating) {
      // Let the parent's once-only crossing state commit before choosing the static endpoint.
      const frame = requestAnimationFrame(() => flight.set(1));
      return () => cancelAnimationFrame(frame);
    }
  }, [isUnlocked, reduced, running, celebrating, celebrationCount, flight]);

  const point = useTransform([geometry, displayed, flight], ([g, p, f]) => position(g, p, f, isUnlocked, reduced));
  return {
    x: useTransform(point, (p) => p.x), y: useTransform(point, (p) => p.y),
    scale: useTransform(point, (p) => p.scale), rotate: useTransform(point, (p) => p.angle),
    opacity: useTransform(geometry, (g) => g ? 1 : 0),
  };
};